import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatGBP, formatApiError } from "../lib/api";
import { Package, Truck, CheckCircle2, Circle } from "lucide-react";

export default function Track() {
  const [params] = useSearchParams();
  const initial = params.get("n") || "";
  const [num, setNum] = useState(initial);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!num) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${num}`);
      setOrder(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (initial) search(); /* eslint-disable-next-line */ }, []);

  return (
    <main className="max-w-3xl mx-auto px-6 py-20" data-testid="track-page">
      <p className="eyebrow text-neutral-600">Royal Mail Tracked</p>
      <h1 className="font-serif text-4xl md:text-5xl mt-2 mb-10">Order tracking</h1>
      <form onSubmit={search} className="flex border-b border-neutral-900 pb-2 mb-10">
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Tracking number (e.g. RMA1B2C3D4E5)"
          data-testid="track-input" className="flex-1 bg-transparent border-none outline-none text-sm uppercase tracking-[0.1em]" />
        <button className="btn-ghost" data-testid="track-submit">{loading ? "Searching…" : "Track"}</button>
      </form>

      {error && <p className="text-sm text-red-700">{error}</p>}

      {order && (
        <div className="border border-hair p-8" data-testid="track-result">
          <div className="grid sm:grid-cols-3 gap-4 mb-6 text-sm">
            <div><p className="eyebrow text-neutral-500">Order</p><p className="mt-1">#{order.id?.slice(0,8).toUpperCase()}</p></div>
            <div><p className="eyebrow text-neutral-500">Total</p><p className="mt-1">{formatGBP(order.total)}</p></div>
            <div><p className="eyebrow text-neutral-500">Tracking</p><p className="mt-1 font-mono">{order.tracking_number}</p></div>
          </div>
          <div className="rule mb-6" />

          <p className="eyebrow text-neutral-600 mb-5">Timeline</p>
          <ol className="relative ml-3 border-l border-[var(--gold-soft)] pl-8 space-y-7" data-testid="timeline">
            {(order.timeline || []).map((evt, i) => {
              const isLast = i === order.timeline.length - 1;
              const Icon = /deliver/i.test(evt.label) ? CheckCircle2 :
                          /ship|dispatch/i.test(evt.label) ? Truck :
                          /payment/i.test(evt.label) ? Package : Circle;
              return (
                <li key={i} className="relative">
                  <span className={`absolute -left-[44px] flex items-center justify-center w-8 h-8 rounded-full ${isLast ? "bg-[var(--gold)] text-white" : "bg-cream border border-hair"}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <p className="font-serif text-lg">{evt.label}</p>
                  <p className="text-xs text-neutral-500 mt-1">{new Date(evt.at).toLocaleString("en-GB")}</p>
                </li>
              );
            })}
          </ol>

          <p className="text-xs text-neutral-500 mt-8">
            For full delivery details, view this parcel on{" "}
            <a className="underline" target="_blank" rel="noreferrer"
               href={`https://www3.royalmail.com/track-your-item#/tracking-results/${order.tracking_number}`}>
              royalmail.com →
            </a>
          </p>
        </div>
      )}
    </main>
  );
}
